import { useEffect, useState } from 'react';
import { serverClock, remainingMs, formatCountdown, fractionLeft } from '@/lib/clock';
import { cn } from '@/lib/cn';

/** Re-render on an interval; returns the current server-clock time. */
export function useServerNow(intervalMs = 250): number {
  const [now, setNow] = useState(() => serverClock.now());
  useEffect(() => {
    const id = setInterval(() => setNow(serverClock.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Plain client clock tick (for elapsed timers that don't depend on the server). */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

export function useRemaining(deadline: string | null | undefined, intervalMs = 250): number {
  const now = useServerNow(intervalMs);
  return remainingMs(deadline, now);
}

export function Countdown({ deadline, className, format = formatCountdown }: { deadline: string | null | undefined; className?: string; format?: (ms: number) => string }) {
  const ms = useRemaining(deadline);
  return (
    <span className={cn('font-semibold tabular-nums', className)} aria-live="off">
      {format(ms)}
    </span>
  );
}

/** Circular countdown ring. `totalMs` = phase length, for the arc. */
export function CountdownRing({
  deadline,
  totalMs,
  size = 64,
  stroke = 6,
  color = 'currentColor',
  className,
  testId,
}: {
  deadline: string | null | undefined;
  totalMs: number;
  size?: number;
  stroke?: number;
  color?: string;
  className?: string;
  testId?: string;
}) {
  const ms = useRemaining(deadline, 100);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const frac = fractionLeft(ms, totalMs);
  const secs = Math.ceil(ms / 1000);
  const urgent = secs <= 3 && ms > 0;
  return (
    <div className={cn('relative inline-flex items-center justify-center', className)} style={{ width: size, height: size }} data-testid={testId} role="timer" aria-label={`${secs} seconds left`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity={0.15} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={urgent ? '#ED4B44' : color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - frac)}
          style={{ transition: 'stroke-dashoffset 120ms linear' }}
        />
      </svg>
      <span className={cn('absolute text-lg font-semibold tabular-nums', urgent && 'text-fg')}>{secs}</span>
    </div>
  );
}
